import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import {
  createOffer as createOfferAPI,
  deleteOffer as deleteOfferAPI,
  getOffers as getOffersAPI,
  updateOffer as updateOfferAPI,
} from 'src/features/companyOffers/companyOffers.api';
import { CompanyOffer } from 'src/features/companyOffers/companyOffers.types';
import { AsyncEntity } from 'src/global';
import createAsyncThunkFromFetch from 'src/store/middlewares/createAsyncThunkFromFetch';

const name = 'offers';

const offersEntityAdapter = createEntityAdapter<AsyncEntity<CompanyOffer>>({
  selectId: offer => offer.langCode,
});

export const offersSelectors = offersEntityAdapter.getSelectors();

export const fetchOffers = createAsyncThunkFromFetch(`${name}/fetchCompanies`, getOffersAPI);
export const deleteOffer = createAsyncThunkFromFetch(`${name}/deleteCompany`, deleteOfferAPI);
export const createOffer = createAsyncThunkFromFetch(`${name}/createCompany`, createOfferAPI);
export const updateOffer = createAsyncThunkFromFetch(`${name}/updateOffer`, updateOfferAPI);

const offersSlice = createSlice({
  name,
  initialState: offersEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchOffers.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchOffers.fulfilled, (state, { payload }) => {
      offersEntityAdapter.upsertMany(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchOffers.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(updateOffer.fulfilled, (state, { meta, payload }) => {
      offersEntityAdapter.updateOne(state, {
        id: meta.arg.code,
        changes: payload,
      });
    });
    builder.addCase(deleteOffer.pending, (state, { meta }) => {
      offersEntityAdapter.updateOne(state, {
        id: meta.arg.code,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteOffer.fulfilled, (state, { meta }) => {
      offersEntityAdapter.removeOne(state, meta.arg.code);
    });
    builder.addCase(deleteOffer.rejected, (state, { meta }) => {
      offersEntityAdapter.updateOne(state, {
        id: meta.arg.code,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createOffer.fulfilled, (state, { payload }) => {
      offersEntityAdapter.addOne(state, payload);
    });
  },
});

const { reducer } = offersSlice;

export default reducer;
